<template>
  <div
    data-cy="cmd-k-session-sidebar"
    class="relative flex h-full shrink-0 flex-col border-r border-neutral-100 bg-neutral-10"
    :class="{ 'transition-[width] duration-200': !isResizing }"
    :style="{ width: `${currentWidth}px` }"
  >
    <div v-if="isExpanded" class="draggable flex min-h-0 flex-1 flex-col">
      <div class="flex flex-col gap-2 px-2 py-3">
        <div class="flex items-center justify-between px-1">
          <button
            data-cy="sidebar-new-session"
            class="text-xs font-medium text-blue-500 hover:text-blue-600"
            @click="emit('session:new')"
          >
            + {{ t("AI_AGENTS.SESSION_SIDEBAR.NEW_SESSION") }}
          </button>
          <button
            data-cy="sidebar-toggle"
            class="flex items-center justify-center rounded p-1 hover:bg-neutral-600/5"
            @click="toggle"
          >
            <icon-base :icon="IconSidebarToggle" class="text-neutral-600" />
          </button>
        </div>
        <search-input
          ref="searchInputRef"
          v-model="searchQuery"
          no-margin
          no-padding
          :delay="300"
          name="sidebar-search"
        />
      </div>
      <div class="flex-1 overflow-y-auto px-2">
        <loader v-if="isLoadingSessions" is-loading size="24" />
        <div
          v-else-if="!filteredSessions.length"
          data-cy="sidebar-empty-state"
          class="px-2 py-4 text-center text-xs text-neutral-600"
        >
          {{ t("AI_AGENTS.SESSION_SIDEBAR.EMPTY_STATE") }}
        </div>
        <sidebar-session-item
          v-for="session in filteredSessions"
          :key="session.id"
          :session="session"
          :is-active="activeSessionId === session.id"
          @click="emit('session:selected', session.id.toString())"
        />
      </div>
    </div>
    <div v-else class="draggable flex flex-col items-center gap-2 p-3">
      <button
        v-tooltip="t('AI_AGENTS.SESSION_SIDEBAR.TOGGLE_SIDEBAR')"
        data-cy="sidebar-toggle"
        class="flex items-center justify-center rounded p-1 hover:bg-neutral-600/5"
        @click="toggle"
      >
        <icon-base :icon="IconSidebarToggle" class="text-neutral-600" />
      </button>
      <button
        v-tooltip="t('AI_AGENTS.SESSION_SIDEBAR.NEW_SESSION')"
        class="flex items-center justify-center rounded p-1 hover:bg-neutral-600/5"
        @click="emit('session:new')"
      >
        <icon-base :icon="IconPlusSmall" class="text-neutral-600" />
      </button>
      <button
        v-tooltip="t('COMMON.SEARCH')"
        class="flex items-center justify-center rounded p-1 hover:bg-neutral-600/5"
        @click="expandAndFocusSearch"
      >
        <icon-base
          :icon="IconSearch"
          view-box="0 0 18 18"
          class="text-neutral-600"
          fill="currentColor"
        />
      </button>
    </div>
    <div
      v-if="isExpanded"
      class="absolute top-0 right-0 bottom-0 z-10 w-1.5 cursor-col-resize hover:bg-blue-400/30 active:bg-blue-400/50"
      @mousedown.stop.prevent="startResize"
    />
  </div>
</template>

<script setup lang="ts">
import { useFuse } from "@vueuse/integrations/useFuse";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { FUZZY_SEARCH_THRESHOLD_DEFAULT } from "@/helpers/constants";
import { useLocalStorageSetting } from "@/hooks/options";
import { useAgentSessionsQuery } from "@/hooks/queries/useAgents";
import { useAiAgentsStore } from "@/stores/ai/agents";

import SidebarSessionItem from "@/components/cmdK/SidebarSessionItem.vue";
import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";
import IconSearch from "@/components/icons/IconSearch.vue";
import IconSidebarToggle from "@/components/icons/IconSidebarToggle.vue";
import IconBase from "@/components/ui/IconBase.vue";
import Loader from "@/components/ui/Loader.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

const COLLAPSED_WIDTH = 48;
const MIN_SIDEBAR_WIDTH = 160;
const DEFAULT_SIDEBAR_WIDTH = 216;

const { maxSidebarWidth, agentId } = defineProps<{
  maxSidebarWidth: number;
  agentId?: string;
}>();

const isExpanded = defineModel<boolean>("isExpanded", { required: true });

const emit = defineEmits<{
  "session:selected": [string];
  "session:new": [];
}>();

const { t } = useI18n();
const aiAgentsStore = useAiAgentsStore();
const { agentSessionInfo } = storeToRefs(aiAgentsStore);

const { data: sessions, isPending: isLoadingSessions } = useAgentSessionsQuery({
  agentId: computed(() => agentId),
});

const searchQuery = ref("");
const searchInputRef = useTemplateRef("searchInputRef");

const sidebarWidth = useLocalStorageSetting(
  "cmdKSidebarWidth",
  DEFAULT_SIDEBAR_WIDTH
);
const isResizing = ref(false);

const currentWidth = computed(() =>
  isExpanded.value
    ? Math.max(MIN_SIDEBAR_WIDTH, Math.min(sidebarWidth.value, maxSidebarWidth))
    : COLLAPSED_WIDTH
);

const activeSessionId = computed(
  () => agentSessionInfo.value.activeSession?.id
);

const allSessions = computed(() => sessions.value ?? []);

const { results: fuseResults } = useFuse(searchQuery, allSessions, {
  fuseOptions: {
    keys: ["first_message.message", "agent.name", "application_name"],
    threshold: FUZZY_SEARCH_THRESHOLD_DEFAULT,
  },
  matchAllWhenSearchEmpty: true,
});

const filteredSessions = computed(() => fuseResults.value.map((r) => r.item));

const startResize = (event: MouseEvent) => {
  isResizing.value = true;
  const startX = event.clientX;
  const startWidth = currentWidth.value;

  const onMouseMove = (e: MouseEvent) => {
    e.preventDefault();
    const delta = e.clientX - startX;
    sidebarWidth.value = Math.max(
      MIN_SIDEBAR_WIDTH,
      Math.min(startWidth + delta, maxSidebarWidth)
    );
  };

  const onMouseUp = () => {
    isResizing.value = false;
    document.body.style.cursor = "";
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  document.body.style.cursor = "col-resize";
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
};

const toggle = () => {
  isExpanded.value = !isExpanded.value;
};

const expandAndFocusSearch = () => {
  isExpanded.value = true;
  nextTick(() => {
    searchInputRef.value?.$el?.querySelector("input")?.focus();
  });
};

watch(isExpanded, (expanded) => {
  if (!expanded) {
    searchQuery.value = "";
  }
});

watch(
  () => maxSidebarWidth,
  (newMax) => {
    if (sidebarWidth.value > newMax) {
      sidebarWidth.value = newMax;
    }
  }
);
</script>
